import { ApiError } from './api-error';
import type { QueuedEvent } from '../types/logistics';

/**
 * The HTTP status behind a rejected request, when there is one.
 *
 * `useLogisticsApi` throws an [ApiError] carrying `httpStatus`, so read that. The regex on the
 * message is a fallback for the errors that are not `ApiError` — a `TypeError` from a dropped
 * connection has no status, and neither does the `Error` thrown on a 401 or a deactivation.
 *
 * **Reading the status off the message was the bug this exists to fix.** `use-offline-sync`
 * classified 404 and 409 with `/\b409\b/.test(err.message)`, and the message is
 * `data.error || "Request failed: " + status` — so for every error the backend describes, which
 * is all of them with the §2.1 envelope, the message is a sentence like "This box has already
 * been scanned" and the number is not in it. Both special cases therefore never fired: a
 * REMOVE_BOX for a box already gone was retried five times and logged as a failure the courier
 * has to look at, and a 409 was retried and then dropped, which is exactly the behaviour the
 * comment above it said it had replaced. The other direction is a false positive: an order id of
 * 409 in an unrelated message would have been read as a conflict and dropped for good.
 */
export function httpStatusOf(err: unknown): number | undefined {
  if (err instanceof ApiError && err.httpStatus !== undefined) return err.httpStatus;
  const message = err instanceof Error ? err.message : '';
  const match = /\b(\d{3})\b/.exec(message);
  if (!match) return undefined;
  const status = Number(match[1]);
  return status >= 100 && status <= 599 ? status : undefined;
}

/**
 * The box a conflicting ADD_BOX collided with, when the backend says it is already on
 * the order we were adding it to.
 *
 * `POST /logistics/orders/{id}/boxes` answers a barcode that is already taken with a 409
 * carrying `{ orderBoxId, orderId, sameOrder }`. `sameOrder` is the part that matters: the
 * uniqueness check on the backend is global, because a barcode belongs to one box
 * anywhere, so "already exists" on its own cannot tell a replay of our own request apart
 * from a real collision with somebody else's order. Returning the id only for the first
 * case is what stops the second being swallowed.
 *
 * Everything here is read defensively — this is a server payload, and an older API that
 * still answers 400 simply returns undefined and the caller retries as before.
 */
export function replayedBoxIdOf(err: unknown): number | undefined {
  if (!(err instanceof ApiError)) return undefined;
  if (err.httpStatus !== 409) return undefined;
  const details = err.details;
  if (!details || details.sameOrder !== true) return undefined;
  const id = details.orderBoxId;
  return typeof id === 'number' && Number.isInteger(id) && id > 0 ? id : undefined;
}

export type QueueFailureVerdict =
  /** Treat as done and remove from the queue. */
  | 'succeeded'
  /** Never going to work — remove it and put it in the failure log for someone to look at. */
  | 'drop'
  /** Bump attempts and try again next time we are online. */
  | 'retry'
  /** Not this event's problem — stop the whole drain. */
  | 'abort';

/**
 * What to do with a queued event whose request just failed.
 *
 * Kept pure and out of the hook because it is the part with the decisions in it, and the hook
 * around it is React Query plumbing that a test cannot reach without standing up most of the app.
 */
export function classifyQueueFailure(type: QueuedEvent['type'], err: unknown): QueueFailureVerdict {
  const message = err instanceof Error ? err.message : '';
  if (message === 'Session expired. Please login again.') return 'abort';

  const status = httpStatusOf(err);

  // A REMOVE_BOX whose box the backend cannot find has already happened. The courier removed
  // it, the queue is replaying it, and the absence *is* the success.
  if (type === 'REMOVE_BOX' && status === 404) return 'succeeded';

  // An ADD_BOX whose barcode is already on *this* order is a replay of a request that
  // got through — the phone never saw the response, so the event stayed queued. The box
  // is on the order and the courier's work is done, which makes this the same shape as
  // the REMOVE_BOX 404 above. It is only a success when the backend says `sameOrder`;
  // the same barcode on someone else's order falls through to the 409 rule below and
  // goes to the failure log, which is where a real collision belongs.
  if (type === 'ADD_BOX' && replayedBoxIdOf(err) !== undefined) return 'succeeded';

  // 409 means the server holds a newer truth — a stale scan, a payment gate, a signature
  // validation, a lock. Retrying cannot change it, so it goes straight to the failure log where
  // someone can see what needs correcting by hand.
  if (status === 409) return 'drop';

  return 'retry';
}
